import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, CheckCircle, Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Marquee from "react-fast-marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const skillOptions = [
  "Teaching/Tutoring", "Mentoring", "Event Planning", "Cooking/Food Prep",
  "Administrative", "Social Media/Marketing", "Photography/Videography",
  "Public Speaking", "Counseling", "First Aid/Medical", "Transportation", "Other"
];

const availabilityOptions = [
  "Weekday Mornings", "Weekday Afternoons", "Weekday Evenings",
  "Weekend Mornings", "Weekend Afternoons", "Weekend Evenings"
];

const interestOptions = [
  "Mentorship Program", "Fresh Start (Back to School)", "Feed the Deserved",
  "Adopt A Youth", "Black College Expo", "General Events"
];

const ethnicityOptions = [
  "African American/Black", "Asian", "Caucasian/White", "Hispanic/Latino",
  "Native American", "Pacific Islander", "Two or More Races", "Prefer Not to Say", "Other"
];

const genderOptions = ["Male", "Female", "Non-binary", "Prefer Not to Say", "Other"];

const VolunteerForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", phone: "", age: "", gender: "", ethnicity: "",
    address: "", city: "", state: "", zip_code: "", occupation: "",
    skills: [], availability: [], interests: [],
    why_volunteer: "", experience: "",
    emergency_contact_name: "", emergency_contact_phone: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayToggle = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = { ...formData, age: parseInt(formData.age) };
      await axios.post(`${API}/volunteer`, submitData);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20 bg-white" data-testid="volunteer-success">
        <div className="card-elevated max-w-lg mx-auto rounded-3xl p-10 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-soar-gold/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-soar-gold" />
          </div>
          <h2 className="font-heading text-3xl text-soar-blue mb-4 tracking-tight">Thank You for Volunteering!</h2>
          <p className="text-soar-blue/60 mb-6">Your volunteer application has been received. We'll review your information and contact you about upcoming volunteer opportunities.</p>
          <p className="font-accent text-3xl text-soar-teal">Together we lift higher!</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="volunteer-form-page" className="bg-white">

      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/7156159/pexels-photo-7156159.jpeg?w=1600" alt="Volunteers" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/95 to-soar-blue/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl space-y-6">
            <span className="inline-block border border-soar-gold/40 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-gold uppercase opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
              Volunteer Program
            </span>
            <h1 className="font-heading text-6xl sm:text-7xl leading-none tracking-tight text-soar-blue opacity-0 animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
              Become a <span className="text-soar-gold">Volunteer</span>
            </h1>
            <p className="text-xl text-soar-blue/60 opacity-0 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
              Make a meaningful difference in the lives of young people. Your time and skills can help shape the next generation of leaders.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4 opacity-0 animate-fade-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
              <div className="text-center p-4 card-elevated rounded-2xl">
                <Heart className="w-6 h-6 text-soar-gold mx-auto mb-2" aria-hidden="true" />
                <p className="text-xs text-soar-blue/60 font-medium">Give Back</p>
              </div>
              <div className="text-center p-4 card-elevated rounded-2xl">
                <Calendar className="w-6 h-6 text-soar-gold mx-auto mb-2" aria-hidden="true" />
                <p className="text-xs text-soar-blue/60 font-medium">Flexible Hours</p>
              </div>
              <div className="text-center p-4 card-elevated rounded-2xl">
                <CheckCircle className="w-6 h-6 text-soar-gold mx-auto mb-2" aria-hidden="true" />
                <p className="text-xs text-soar-blue/60 font-medium">Make Impact</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-5" style={{ backgroundColor: '#212163' }}>
        <Marquee speed={50} gradient={false}>
          {["SERVE", "LEAD", "EMPOWER", "CONNECT", "GROW", "GIVE"].map((w) => (
            <span key={w} className="font-heading text-4xl mx-12 tracking-wider" style={{ color: 'rgba(221,182,77,0.3)' }}>{w}</span>
          ))}
        </Marquee>
      </div>

      {/* Application Form */}
      <section className="py-20" data-testid="volunteer-form-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="card-elevated rounded-3xl p-8 sm:p-10">
            <h2 className="font-heading text-3xl text-soar-blue mb-2 tracking-tight">Volunteer Application Form</h2>
            <p className="text-soar-blue/50 mb-8">Fill out the form below to join our volunteer team. All fields marked with * are required.</p>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Personal Information</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-soar-blue/70">First Name *</Label>
                    <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-first-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-soar-blue/70">Last Name *</Label>
                    <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-last-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-soar-blue/70">Email *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-soar-blue/70">Phone *</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-soar-blue/70">Age *</Label>
                    <Input id="age" name="age" type="number" min="18" value={formData.age} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-age" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="occupation" className="text-soar-blue/70">Occupation *</Label>
                    <Input id="occupation" name="occupation" value={formData.occupation} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-occupation" />
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Demographics</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gender" className="text-soar-blue/70">Gender *</Label>
                    <Select value={formData.gender} onValueChange={(value) => handleSelectChange("gender", value)}>
                      <SelectTrigger className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent className="bg-soar-blue border-soar-blue/10">
                        {genderOptions.map(option => (<SelectItem key={option} value={option}>{option}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ethnicity" className="text-soar-blue/70">Ethnicity *</Label>
                    <Select value={formData.ethnicity} onValueChange={(value) => handleSelectChange("ethnicity", value)}>
                      <SelectTrigger className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-ethnicity">
                        <SelectValue placeholder="Select ethnicity" />
                      </SelectTrigger>
                      <SelectContent className="bg-soar-blue border-soar-blue/10">
                        {ethnicityOptions.map(option => (<SelectItem key={option} value={option}>{option}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Address</h3>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-soar-blue/70">Street Address</Label>
                    <Input id="address" name="address" value={formData.address} onChange={handleChange} className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-address" />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-soar-blue/70">City *</Label>
                      <Input id="city" name="city" value={formData.city} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-city" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state" className="text-soar-blue/70">State *</Label>
                      <Input id="state" name="state" value={formData.state} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-state" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip_code" className="text-soar-blue/70">ZIP Code *</Label>
                      <Input id="zip_code" name="zip_code" value={formData.zip_code} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-zip" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Skills & Abilities</h3>
                <p className="text-sm text-soar-blue/50 mb-4">Select the skills you can contribute:</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {skillOptions.map((skill) => (
                    <label key={skill} htmlFor={`skill-${skill}`} className="flex items-center space-x-3 p-3 rounded-xl border border-soar-blue/10 hover:border-soar-blue/20 cursor-pointer bg-soar-blue/5 transition-colors">
                      <input type="checkbox" id={`skill-${skill}`} checked={formData.skills.includes(skill)} onChange={() => handleArrayToggle("skills", skill)} className="h-4 w-4 rounded border-white/30 text-soar-teal focus:ring-soar-teal bg-soar-blue/10" />
                      <span className="text-sm text-soar-blue/80">{skill}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Availability</h3>
                <p className="text-sm text-soar-blue/50 mb-4">When are you typically available to volunteer?</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {availabilityOptions.map((time) => (
                    <label key={time} htmlFor={`avail-${time}`} className="flex items-center space-x-3 p-3 rounded-xl border border-soar-blue/10 hover:border-soar-blue/20 cursor-pointer bg-soar-blue/5 transition-colors">
                      <input type="checkbox" id={`avail-${time}`} checked={formData.availability.includes(time)} onChange={() => handleArrayToggle("availability", time)} className="h-4 w-4 rounded border-white/30 text-soar-teal focus:ring-soar-teal bg-soar-blue/10" />
                      <span className="text-sm text-soar-blue/80">{time}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Program Interests */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Program Interests</h3>
                <p className="text-sm text-soar-blue/50 mb-4">Which programs are you interested in volunteering for?</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {interestOptions.map((interest) => (
                    <label key={interest} htmlFor={`vol-interest-${interest}`} className="flex items-center space-x-3 p-3 rounded-xl border border-soar-blue/10 hover:border-soar-blue/20 cursor-pointer bg-soar-blue/5 transition-colors">
                      <input type="checkbox" id={`vol-interest-${interest}`} checked={formData.interests.includes(interest)} onChange={() => handleArrayToggle("interests", interest)} className="h-4 w-4 rounded border-white/30 text-soar-teal focus:ring-soar-teal bg-soar-blue/10" />
                      <span className="text-sm text-soar-blue/80">{interest}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Why Volunteer */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="why_volunteer" className="text-soar-blue/70">Why do you want to volunteer with us? *</Label>
                  <Textarea id="why_volunteer" name="why_volunteer" value={formData.why_volunteer} onChange={handleChange} required className="rounded-xl min-h-[100px] bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" placeholder="Tell us what motivates you to volunteer..." data-testid="volunteer-why" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="experience" className="text-soar-blue/70">Previous Volunteer Experience</Label>
                  <Textarea id="experience" name="experience" value={formData.experience} onChange={handleChange} className="rounded-xl min-h-[100px] bg-soar-blue/5 border-soar-blue/10 text-soar-blue placeholder:text-white/20" placeholder="Describe any relevant volunteer or work experience..." data-testid="volunteer-experience" />
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="font-heading text-xl text-soar-blue mb-4">Emergency Contact</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_name" className="text-soar-blue/70">Contact Name *</Label>
                    <Input id="emergency_contact_name" name="emergency_contact_name" value={formData.emergency_contact_name} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-emergency-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact_phone" className="text-soar-blue/70">Contact Phone *</Label>
                    <Input id="emergency_contact_phone" name="emergency_contact_phone" type="tel" value={formData.emergency_contact_phone} onChange={handleChange} required className="rounded-xl bg-soar-blue/5 border-soar-blue/10 text-white" data-testid="volunteer-emergency-phone" />
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={submitting} className="w-full rounded-full bg-soar-gold hover:bg-soar-gold/90 text-[#0a0a2e] py-6 text-lg font-bold hover:scale-[1.02] transition-transform duration-200" data-testid="volunteer-submit-btn">
                {submitting ? "Submitting..." : (<>Submit Application <ArrowRight className="w-5 h-5 ml-2" /></>)}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VolunteerForm;
